/**
 * Sentry initialization — production-grade error monitoring.
 *
 * Design:
 * - Only initializes in production builds (skipped in dev to avoid noise).
 * - Skipped inside Lovable preview iframes (avoids polluting issues with editor traffic).
 * - Honors analytics consent: events are dropped in beforeSend if user did not opt-in.
 * - Session Replay: 0% on normal sessions, 100% on sessions with errors (cheap + useful).
 * - Performance tracing: 10% sample to stay well within free tier (5k events/mo).
 *
 * The DSN is public-by-design (Sentry SDK requires it client-side).
 */
import * as Sentry from "@sentry/react";
import { consentAllowsAnalytics } from "@/lib/consent";

const DSN = "https://90088a7f3d0298668ed140a7f95d1e4e@o4511242826547200.ingest.de.sentry.io/4511242832838736";

function isInsidePreviewIframe(): boolean {
  try {
    if (window.self === window.top) return false;
    const host = window.location.hostname;
    return host.includes("lovable.app") || host.includes("lovableproject.com");
  } catch {
    // Cross-origin access throws → we're definitely in an iframe
    return true;
  }
}

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (import.meta.env.MODE !== "production") return;
  if (isInsidePreviewIframe()) return;

  initialized = true;

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    release: `arkeonx@${import.meta.env.VITE_APP_VERSION ?? "dev"}`,
    sendDefaultPii: false, // GDPR: never auto-collect IP/email
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1, // 10% transactions
    replaysSessionSampleRate: 0, // 0% normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% sessions that hit an error
    // Drop noisy errors before sending
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      // Browser extensions
      /extension\//i,
      /^chrome:\/\//i,
    ],
    beforeSend(event) {
      // Strict consent gate
      if (!consentAllowsAnalytics()) return null;
      return event;
    },
  });
}

export { Sentry };
